import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { authAPI, securityAPI, companiesAPI } from "../utils/api";
import { debugLogin, testBackendConnection } from "../utils/debugLogin";
import logoBlack from "../logo_black.png";
import logoWhite from "../logo_white.png";
import AnimatedModal from "./AnimatedModal";
import { FiEye, FiEyeOff, FiCheckCircle, FiXCircle } from "react-icons/fi";
import PhoneCountrySelect from "./PhoneCountrySelect";
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from 'libphonenumber-js';

export default function WelcomeScreen() {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to true (dark mode)
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : true;
  });
  
  // Login form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Signup form states
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupCompany, setSignupCompany] = useState("");
  const [signupCompanyType, setSignupCompanyType] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [signupPhoneCountry, setSignupPhoneCountry] = useState('US');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [signupCompanyExists, setSignupCompanyExists] = useState(null);
  const [checkingSignupCompany, setCheckingSignupCompany] = useState(false);
  const signupCompanyDebounceRef = useRef(null);
  const signupCompanyRequestIdRef = useRef(0);
  const lastSignupCompanyQueriedRef = useRef('');

  const signupEmailValid = useMemo(() => {
    const v = (signupEmail || '').trim();
    if (!v) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, [signupEmail]);

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);

  const { login, register, logout } = useAuth();

  // Country lists and phone validation for signup
  const countryDisplay = useMemo(() => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' });
    } catch { return null; }
  }, []);

  const countriesWithCodes = useMemo(() => {
    try {
      return getCountries()
        .map(c => ({ code: c, dial: `+${getCountryCallingCode(c)}`, name: countryDisplay ? countryDisplay.of(c) : c }))
        .sort((a,b) => (a.name || a.code).localeCompare(b.name || b.code));
    } catch { return []; }
  }, [countryDisplay]);

  

  const signupPhoneValidation = useMemo(() => {
    const national = (signupPhone || '').replace(/\D/g, '');
    if (!signupPhoneCountry || national.length === 0) return { valid: false, e164: null };
    try {
      const pn = parsePhoneNumberFromString(national, signupPhoneCountry);
      if (pn && pn.isValid()) return { valid: true, e164: pn.number };
    } catch {}
    return { valid: false, e164: null };
  }, [signupPhone, signupPhoneCountry]);

  // Handle dark mode toggle
  useEffect(() => {
    // Save preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    setGoogleLoading(true);
    setError("");

    try {
      // Send the ID token to your backend
      const result = await authAPI.loginWithGoogle(response.credential);
      
      // Use auth context to manage login with new JWT structure
      login(result);
    } catch (error) {
      console.error("Google login failed:", error);
      
      if (error.data && error.data.message) {
        setError(error.data.message);
      } else if (error.message && error.message.includes('Failed to fetch')) {
        setError("Unable to connect to the server. Please check your internet connection.");
      } else {
        setError("Google login failed. Please try again.");
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [login]);

  const initializeGoogleOAuth = useCallback(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    // if (!clientId || clientId === 'your-google-client-id') {
    //   console.warn('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file');
    //   setError("Google OAuth is not configured. Please contact support.");
    //   return;
    // }

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      console.log('Google OAuth initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google OAuth:', error);
      setError("Failed to initialize Google OAuth. Please refresh the page.");
    }
  }, [handleGoogleCredentialResponse]);

  // Initialize Google OAuth
  useEffect(() => {
    // Check if Google OAuth is already loaded
    if (window.google && window.google.accounts) {
      initializeGoogleOAuth();
      return;
    }

    // Make the callback function globally available
    window.handleGoogleCredentialResponse = handleGoogleCredentialResponse;
    
    // Load Google OAuth script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.google && window.google.accounts) {
        initializeGoogleOAuth();
      }
    };

    script.onerror = () => {
      console.error('Failed to load Google OAuth script');
      setError("Failed to load Google OAuth. Please check your internet connection.");
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script if component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      // Cleanup global callback
      delete window.handleGoogleCredentialResponse;
    };
  }, [handleGoogleCredentialResponse, initializeGoogleOAuth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await authAPI.login({ 
        email: loginEmail, 
        password: loginPassword 
      });
      
      login(response);
    } catch (error) {
      setError(error.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch password policy when switching to Sign Up
  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        setPolicyLoading(true);
        const policy = await securityAPI.getPasswordPolicy();
        setPasswordPolicy(policy);
      } catch (e) {
        setPasswordPolicy(null);
      } finally {
        setPolicyLoading(false);
      }
    };
    if (!isLoginMode && passwordPolicy === null && !policyLoading) {
      fetchPolicy();
    }
  }, [isLoginMode, passwordPolicy, policyLoading]);

  const signupPasswordChecks = useMemo(() => {
    const pwd = signupPassword || "";
    const confirm = signupConfirmPassword || "";
    const policy = passwordPolicy;
    const rules = [];
    if (!policy || policy.isEnabled === false) return rules;

    if (typeof policy.minLength === 'number' && policy.minLength > 0) {
      const ok = pwd.length >= policy.minLength;
      rules.push({ key: 'minLength', ok, label: `At least ${policy.minLength} characters`, detail: `${pwd.length}/${policy.minLength}` });
    }
    if (typeof policy.maxLength === 'number' && policy.maxLength > 0) {
      const ok = pwd.length <= policy.maxLength;
      rules.push({ key: 'maxLength', ok, label: `No more than ${policy.maxLength} characters`, detail: `${pwd.length}/${policy.maxLength}` });
    }
    if (typeof policy.minUppercaseCount === 'number' && policy.minUppercaseCount > 0) {
      const uppercaseCount = (pwd.match(/[A-Z]/g) || []).length;
      const ok = uppercaseCount >= policy.minUppercaseCount;
      rules.push({ key: 'uppercase', ok, label: `At least ${policy.minUppercaseCount} uppercase letter${policy.minUppercaseCount>1?'s':''}`, detail: `${uppercaseCount}/${policy.minUppercaseCount}` });
    }
    if (typeof policy.minNumberCount === 'number' && policy.minNumberCount > 0) {
      const numberCount = (pwd.match(/[0-9]/g) || []).length;
      const ok = numberCount >= policy.minNumberCount;
      rules.push({ key: 'number', ok, label: `At least ${policy.minNumberCount} number${policy.minNumberCount>1?'s':''}`, detail: `${numberCount}/${policy.minNumberCount}` });
    }
    if (typeof policy.minSpecialCount === 'number' && policy.minSpecialCount > 0) {
      const specialCount = (pwd.match(/[^A-Za-z0-9]/g) || []).length;
      const ok = specialCount >= policy.minSpecialCount;
      rules.push({ key: 'special', ok, label: `At least ${policy.minSpecialCount} special character${policy.minSpecialCount>1?'s':''}`, detail: `${specialCount}/${policy.minSpecialCount}` });
    }
    if (confirm.length > 0 || pwd.length > 0) {
      const ok = pwd.length > 0 && confirm === pwd;
      rules.push({ key: 'match', ok, label: `Passwords match` });
    }
    return rules;
  }, [signupPassword, signupConfirmPassword, passwordPolicy]);

  const doesSignupPasswordMeetPolicy = () => {
    if (!passwordPolicy || passwordPolicy.isEnabled === false) return true;
    const pwd = signupPassword || "";
    const policy = passwordPolicy;
    if (typeof policy.minLength === 'number' && policy.minLength > 0 && pwd.length < policy.minLength) return false;
    if (typeof policy.maxLength === 'number' && policy.maxLength > 0 && pwd.length > policy.maxLength) return false;
    if (typeof policy.minUppercaseCount === 'number' && policy.minUppercaseCount > 0) {
      if ((pwd.match(/[A-Z]/g) || []).length < policy.minUppercaseCount) return false;
    }
    if (typeof policy.minNumberCount === 'number' && policy.minNumberCount > 0) {
      if ((pwd.match(/[0-9]/g) || []).length < policy.minNumberCount) return false;
    }
    if (typeof policy.minSpecialCount === 'number' && policy.minSpecialCount > 0) {
      if ((pwd.match(/[^A-Za-z0-9]/g) || []).length < policy.minSpecialCount) return false;
    }
    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (signupPassword !== signupConfirmPassword) {
      setError("Passwords don't match");
      setIsLoading(false);
      return;
    }

    // Names must be alphabets only
    const nameRegex = /^[A-Za-z\s]+$/;
    if (!nameRegex.test(signupFirstName) || !nameRegex.test(signupLastName)) {
      setError("First and Last name must contain letters only");
      setIsLoading(false);
      return;
    }

    // Validate email format
    if (!signupEmailValid) {
      setError("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    // Validate phone
    if (!signupPhoneCountry || !signupPhoneValidation.valid) {
      setError("Please enter a valid phone number for the selected code");
      setIsLoading(false);
      return;
    }

    if (!doesSignupPasswordMeetPolicy()) {
      setError("Password does not meet the policy requirements.");
      setIsLoading(false);
      return;
    }

    if (!agreedToTerms) {
      setError("Please agree to the Terms and Conditions");
      setIsLoading(false);
      return;
    }

    try {
      const userData = {
        firstName: signupFirstName,
        lastName: signupLastName,
        email: signupEmail,
        phoneNumber: signupPhoneValidation.e164,
        phoneCode: `+${getCountryCallingCode(signupPhoneCountry)}`,
        phoneCountry: signupPhoneCountry,
        companyName: signupCompany,
        companyType: signupCompanyType,
        password: signupPassword
      };

      const response = await authAPI.register(userData);
      register(response);
      // After registration, clear any session and go to login screen
      logout();
      setIsLoginMode(true);
    } catch (error) {
      setError(error.message || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    console.log('Google Login Debug Info:', {
      clientId: clientId ? 'Set' : 'Not set',
      googleLoaded: !!(window.google && window.google.accounts),
      googleAccountsId: !!(window.google && window.google.accounts && window.google.accounts.id)
    });
    
    if (!clientId || clientId === 'your-google-client-id') {
      setError("Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file");
      return;
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        window.google.accounts.id.prompt();
      } catch (error) {
        console.error('Failed to prompt Google OAuth:', error);
        setError("Failed to open Google login. Please try again.");
      }
    } else {
      setError("Google OAuth not loaded. Please refresh the page and try again.");
    }
  };

  const handleGoogleSignup = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!clientId || clientId === 'your-google-client-id') {
      setError("Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file");
      return;
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        window.google.accounts.id.prompt();
      } catch (error) {
        console.error('Failed to prompt Google OAuth:', error);
        setError("Failed to open Google signup. Please try again.");
      }
    } else {
      setError("Google OAuth not loaded. Please refresh the page and try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col relative">
      {/* Floating Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/5 rounded-full animate-pulse blur-3xl" style={{animationDuration: '8s'}}></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full animate-pulse blur-3xl" style={{animationDuration: '12s', animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-green-500/5 rounded-full animate-bounce blur-2xl" style={{animationDuration: '6s'}}></div>
      </div>
      {/* Dark Mode Toggle Button */}
      <button
        onClick={() => setIsDarkMode(!isDarkMode)}
        className="dark-mode-toggle p-3 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-600 hover:shadow-xl transition-all duration-300 cursor-pointer simple-fade-in"
        title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        style={{ 
          animationDelay: '0.1s'
        }}
      >
          {isDarkMode ? (
            // Sun icon for light mode
            <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
            </svg>
          ) : (
            // Moon icon for dark mode
            <svg className="w-6 h-6 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
      </button>

      {/* Mobile Header - Logo and Title (visible only on mobile) */}
      <div className="block md:hidden text-center py-4 px-4 flex-shrink-0 simple-fade-in">
        <div className="flex justify-center mb-3 simple-slide-up" style={{animationDelay: '0.1s'}}>
          <div className="logo-pulse">
            <img 
              src={isDarkMode ? logoWhite : logoBlack} 
              alt="Company Logo" 
              className="h-12 w-auto object-contain filter drop-shadow-lg transition-transform duration-300 hover:scale-105"
            />
            <div className="logo-scanner"></div>
          </div>
        </div>
        <h1 className="text-2xl font-bold mb-1 text-gray-800 dark:text-gray-100 tracking-wide logo-text-animate" style={{animationDelay: '0.2s'}}>
          ENGINEERING AI
        </h1>
        <p className="text-blue-600 dark:text-blue-400 text-sm font-medium tracking-wider uppercase subtitle-glow" style={{animationDelay: '0.3s'}}>
          Chat with Your Documents
        </p>
      </div>

      {/* Desktop Layout Container */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Left Side - Authentication Forms (scrollable, scrollbar hidden) */}
        <div className={`flex-1 max-h-screen overflow-y-auto hide-scrollbar flex ${isLoginMode ? 'items-start md:items-center' : 'items-start md:items-start'} justify-center p-4 md:p-8 simple-fade-in`} style={{animationDelay: '0.4s'}}>
          <div className="w-full max-w-md simple-slide-up" style={{animationDelay: '0.5s'}}>
          {/* Form Toggle */}
          <div className="flex mb-6 md:mb-8 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 transition-all duration-300">
            <button
              onClick={() => setIsLoginMode(true)}
              className={`flex-1 py-2 px-3 md:px-4 rounded-md text-sm font-medium transition-all duration-300 ${
                isLoginMode
                  ? "bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm transform scale-[1.02]"
                  : "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600/50"
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setIsLoginMode(false)}
              className={`flex-1 py-2 px-3 md:px-4 rounded-md text-sm font-medium transition-all duration-300 ${
                !isLoginMode
                  ? "bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm transform scale-[1.02]"
                  : "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600/50"
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg text-sm animate-in slide-in-from-top-2 fade-in duration-300">
              {error}
            </div>
          )}

          {/* Form Container with Dynamic Height */}
          <div className="transition-all duration-500 ease-in-out overflow-visible p-1 form-container-height">

          {/* Login Form */}
          {isLoginMode ? (
            <div className="transition-all duration-500 ease-in-out transform">
              <form onSubmit={handleLogin} className="space-y-3 md:space-y-4 animate-in slide-in-from-left-5 fade-in duration-500">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 md:mb-6">Welcome Back</h2>
                
                <div className="space-y-4">
                  <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showLoginPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(v => !v)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-300"
                        aria-label={showLoginPassword ? "Hide password" : "Show password"}
                      >
                        {showLoginPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Forgot Password link - always visible */}
                <div className="mb-2 text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(true)}
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 hover:shadow-lg disabled:cursor-not-allowed"
                >
                  {isLoading ? "Signing In..." : "Sign In"}
                </button>

                {/* Debug button - remove in production */}
                {process.env.NODE_ENV === 'development' && (
                  <button
                    type="button"
                    onClick={async () => {
                      console.log('🧪 Testing backend connection...');
                      try {
                        await testBackendConnection();
                        console.log('🧪 Testing debug login...');
                        await debugLogin('test@test.com', 'test123');
                      } catch (error) {
                        console.error('🧪 Debug test failed:', error);
                      }
                    }}
                    className="w-full mt-2 bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 hover:shadow-lg"
                  >
                    🧪 Debug: Test Backend Connection
                  </button>
                )}

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gradient-to-br from-indigo-50 via-white to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-gray-500 dark:text-gray-400">Or continue with</span>
                  </div>
                </div>

                {/* Google Login Button */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {googleLoading ? "Signing in..." : "Continue with Google"}
                </button>
              </form>
            </div>
          ) : (
            /* Signup Form */
            <div className="transition-all duration-500 ease-in-out transform">
              <form onSubmit={handleSignup} className="space-y-4 animate-in slide-in-from-right-5 fade-in duration-500">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Create Account</h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={signupFirstName}
                        onChange={(e) => setSignupFirstName(e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500"
                        placeholder="First name"
                      />
                    </div>
                    <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={signupLastName}
                        onChange={(e) => setSignupLastName(e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500"
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                      className={`w-full px-3 py-2 border ${signupEmail && !signupEmailValid ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500`}
                      placeholder="Enter your email"
                    />
                    {signupEmail && !signupEmailValid && (
                      <div className="mt-1 text-xs text-red-600">Invalid email address</div>
                    )}
                  </div>

                  <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone Number
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-1">
                        <PhoneCountrySelect value={signupPhoneCountry} onChange={setSignupPhoneCountry} />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="tel"
                          value={signupPhone}
                          onChange={(e) => setSignupPhone(e.target.value.replace(/\D/g, ''))}
                          required
                          className={`w-full px-3 py-2 border ${signupPhoneValidation.valid ? 'border-green-400' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500`}
                          placeholder="Enter your phone number"
                        />
                        {!signupPhoneValidation.valid && signupPhone && (
                          <div className="mt-1 text-xs text-red-600">Invalid phone number</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={signupCompany}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSignupCompany(v);
                        if (signupCompanyDebounceRef.current) {
                          clearTimeout(signupCompanyDebounceRef.current);
                        }
                        const trimmed = (v || '').trim();
                        if (!trimmed || trimmed.length < 2) {
                          setSignupCompanyExists(null);
                          setCheckingSignupCompany(false);
                          return;
                        }
                        setCheckingSignupCompany(true);
                        signupCompanyRequestIdRef.current += 1;
                        const reqId = signupCompanyRequestIdRef.current;
                        lastSignupCompanyQueriedRef.current = trimmed;
                        signupCompanyDebounceRef.current = setTimeout(async () => {
                          try {
                            const res = await companiesAPI.existsByName(trimmed);
                            if (reqId !== signupCompanyRequestIdRef.current) return;
                            if (trimmed !== lastSignupCompanyQueriedRef.current) return;
                            setSignupCompanyExists(!!res.exists);
                          } catch (_) {
                            if (reqId !== signupCompanyRequestIdRef.current) return;
                            setSignupCompanyExists(null);
                          } finally {
                            if (reqId === signupCompanyRequestIdRef.current) setCheckingSignupCompany(false);
                          }
                        }, 400);
                      }}
                      required
                      className={`w-full px-3 py-2 border ${signupCompanyExists === true ? 'border-yellow-400' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500`}
                      placeholder="Enter your company name"
                    />
                    {signupCompany && (
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2">
                        {checkingSignupCompany && <span className="animate-pulse">Checking…</span>}
                        {!checkingSignupCompany && signupCompanyExists === true && <span className="text-amber-500">Company exists</span>}
                        {!checkingSignupCompany && signupCompanyExists === false && <span className="text-green-600">Available</span>}
                      </div>
                    )}
                  </div>

                  <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company Type
                    </label>
                    {signupCompanyExists === false && (
                      <select
                        value={signupCompanyType}
                        onChange={(e) => setSignupCompanyType(e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500"
                      >
                        <option value="">Select company type</option>
                        <option value="contractor">Contractor</option>
                        <option value="consultant">Consultant</option>
                        <option value="government">Government</option>
                        <option value="other">Other</option>
                      </select>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showSignupPassword ? "text" : "password"}
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          required
                          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500"
                          placeholder="Password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword(v => !v)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-300"
                          aria-label={showSignupPassword ? "Hide password" : "Show password"}
                        >
                          {showSignupPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                    </div>
                    <div className="transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/10">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={showSignupConfirmPassword ? "text" : "password"}
                          value={signupConfirmPassword}
                          onChange={(e) => setSignupConfirmPassword(e.target.value)}
                          required
                          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500"
                          placeholder="Confirm password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupConfirmPassword(v => !v)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-300"
                          aria-label={showSignupConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                        >
                          {showSignupConfirmPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {passwordPolicy && passwordPolicy.isEnabled && (
                    <div className="mt-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">Password requirements:</div>
                      <ul className="space-y-1 text-sm">
                        {signupPasswordChecks.map(rule => (
                          <li key={rule.key} className={`flex items-center gap-2 ${rule.ok ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                            {rule.ok ? <FiCheckCircle className="text-green-500" /> : <FiXCircle className="text-gray-400" />}
                            <span>
                              {rule.label}
                              {rule.detail ? ` (${rule.detail})` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Terms & Conditions */}
                <div className="-mt-2">
                  <label className="inline-flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="checkbox" className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" checked={agreedToTerms} onChange={(e)=>setAgreedToTerms(e.target.checked)} />
                    <span>
                      I agree to the{' '}
                      <button type="button" onClick={()=>setShowTerms(true)} className="text-indigo-600 hover:text-indigo-500 underline underline-offset-2">Terms and Conditions</button>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 hover:shadow-lg disabled:cursor-not-allowed"
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </button>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gradient-to-br from-indigo-50 via-white to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-gray-500 dark:text-gray-400">Or continue with</span>
                  </div>
                </div>

                {/* Google Signup Button */}
                <button
                  type="button"
                  onClick={handleGoogleSignup}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {googleLoading ? "Signing up..." : "Sign up with Google"}
                </button>
              </form>
            </div>
          )}
          </div>
        </div>
        </div>

        {/* Right Side - Company Info (fixed, non-scrolling) */}
        <div className="hidden md:flex flex-1 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden sticky top-0 self-start" style={{height: '100vh'}}>
          {/* Subtle Background Animation */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-transparent to-purple-900/10 animate-pulse"></div>
          
          {/* Floating Geometric Shapes */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-32 h-32 bg-blue-500/5 rounded-full animate-bounce" style={{animationDuration: '6s'}}></div>
            <div className="absolute bottom-32 left-16 w-24 h-24 bg-purple-500/5 rounded-lg rotate-45 animate-pulse" style={{animationDuration: '4s'}}></div>
            <div className="absolute top-1/2 right-8 w-16 h-16 bg-green-500/5 rounded-full animate-ping" style={{animationDuration: '8s'}}></div>
          </div>
          
          <div className="flex flex-col w-full p-8 text-white relative z-10">
            {/* Animated Logo Section */}
            <div className="text-center mb-8 simple-fade-in breathe" style={{animationDelay: '0.2s'}}>
              <div className="inline-block mb-4 logo-container float-gentle">
                <div className="logo-pulse relative">
                  <img 
                    src={isDarkMode ? logoWhite : logoWhite} 
                    alt="Company Logo" 
                    className="h-16 w-auto object-contain mx-auto filter drop-shadow-lg transition-transform duration-300 hover:scale-105"
                  />
                  <div className="logo-scanner"></div>
                </div>
              </div>
              <h1 className="text-3xl font-bold mb-2 text-white tracking-wide logo-text-animate">
                ENGINEERING AI
              </h1>
              <p className="text-blue-400 text-sm font-medium tracking-wider uppercase subtitle-glow">
                Chat with Your Documents
              </p>
            </div>

            {/* Animated Features Grid */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center enhanced-hover transition-all duration-300 simple-slide-up" style={{animationDelay: '0.4s'}}>
                <div className="w-12 h-12 mx-auto mb-3 bg-blue-500/20 rounded-lg flex items-center justify-center relative overflow-hidden breathe">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent translate-x-[-100%] animate-pulse"></div>
                  <svg className="w-6 h-6 text-blue-400 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h3 className="font-semibold text-sm mb-1">AI Analysis</h3>
                <p className="text-gray-400 text-xs">Intelligent processing</p>
              </div>
              
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center enhanced-hover transition-all duration-300 simple-slide-up" style={{animationDelay: '0.6s'}}>
                <div className="w-12 h-12 mx-auto mb-3 bg-green-500/20 rounded-lg flex items-center justify-center relative overflow-hidden breathe" style={{animationDelay: '1s'}}>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/20 to-transparent translate-x-[-100%] animate-pulse" style={{animationDelay: '1s'}}></div>
                  <svg className="w-6 h-6 text-green-400 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <h3 className="font-semibold text-sm mb-1">Team Sync</h3>
                <p className="text-gray-400 text-xs">Real-time collaboration</p>
              </div>
              
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center enhanced-hover transition-all duration-300 simple-slide-up" style={{animationDelay: '0.8s'}}>
                <div className="w-12 h-12 mx-auto mb-3 bg-purple-500/20 rounded-lg flex items-center justify-center relative overflow-hidden breathe" style={{animationDelay: '2s'}}>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/20 to-transparent translate-x-[-100%] animate-pulse" style={{animationDelay: '2s'}}></div>
                  <svg className="w-6 h-6 text-purple-400 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  </svg>
                </div>
                <h3 className="font-semibold text-sm mb-1">Secure</h3>
                <p className="text-gray-400 text-xs">Enterprise security</p>
              </div>
            </div>

            {/* Animated Demo Section */}
            <div className="flex-1 flex items-center justify-center mb-8">
              <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-6 w-full max-w-md simple-slide-up transition-all duration-500 hover:bg-gray-800/40 hover:border-gray-600" style={{animationDelay: '1s'}}>
                <div className="text-center mb-4 simple-fade-in" style={{animationDelay: '1.2s'}}>
                  <h3 className="text-lg font-semibold mb-2 flex items-center justify-center gap-2">
                    <span className="animate-pulse">🤖</span>
                    Interactive AI Assistant
                  </h3>
                  <p className="text-gray-400 text-sm">Upload documents and get instant insights</p>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-3 text-center simple-hover-lift transition-all duration-300" style={{animationDelay: '1.4s'}}>
                    <div className="text-2xl mb-2 animate-bounce" style={{animationDuration: '3s'}}>📄</div>
                    <div className="text-sm text-gray-300">technical_manual.pdf</div>
                    <div className="text-xs text-green-400 mt-1 flex items-center justify-center gap-1">
                      <span className="animate-ping w-1 h-1 bg-green-400 rounded-full"></span>
                      ✓ Processed
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3 simple-slide-up transition-all duration-300 hover:bg-blue-500/30" style={{animationDelay: '1.6s'}}>
                      <div className="text-sm text-blue-200">"What are the safety requirements?"</div>
                    </div>
                    
                    <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-3 simple-slide-up transition-all duration-300 hover:bg-gray-700/70" style={{animationDelay: '1.8s'}}>
                      <div className="flex items-start gap-2">
                        <div className="flex gap-1 mt-1">
                          <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></div>
                          <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                        <div className="text-sm text-gray-300">Based on your manual, pressure vessels must comply with ASME Section VIII...</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Animated Stats Section */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center simple-slide-up transition-all duration-300 hover:bg-gray-800/70 hover:border-gray-600 hover:scale-105" style={{animationDelay: '2s'}}>
                <div className="text-2xl font-bold text-blue-400 mb-1 relative">
                  50K+
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="text-gray-400 text-sm">Active Systems</div>
              </div>
              
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center simple-slide-up transition-all duration-300 hover:bg-gray-800/70 hover:border-gray-600 hover:scale-105" style={{animationDelay: '2.2s'}}>
                <div className="text-2xl font-bold text-green-400 mb-1 relative">
                  99.9%
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="text-gray-400 text-sm">Uptime</div>
              </div>
              
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center simple-slide-up transition-all duration-300 hover:bg-gray-800/70 hover:border-gray-600 hover:scale-105" style={{animationDelay: '2.4s'}}>
                <div className="text-2xl font-bold text-purple-400 mb-1 relative">
                  24/7
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="text-gray-400 text-sm">Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Section - Demo and Features (visible only on mobile) */}
      <div className="block md:hidden px-4 pb-6 flex-shrink-0">
        {/* Animated Simple Demo */}
        <div className="bg-gray-800/20 dark:bg-gray-800/40 border border-gray-300 dark:border-gray-600 rounded-xl p-4 mb-6 shadow-sm simple-slide-up transition-all duration-500 hover:shadow-lg" style={{animationDelay: '0.3s'}}>
          <div className="text-center mb-4 simple-fade-in" style={{animationDelay: '0.5s'}}>
            <div className="text-2xl mb-2 animate-bounce" style={{animationDuration: '3s'}}>🤖</div>
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">AI Assistant</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Upload documents and chat</p>
          </div>
          
          <div className="space-y-3">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center simple-hover-lift transition-all duration-300" style={{animationDelay: '0.7s'}}>
              <div className="text-lg mb-1 animate-pulse">📄</div>
              <div className="text-sm text-gray-700 dark:text-gray-300">manual.pdf</div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center justify-center gap-1">
                <span className="animate-ping w-1 h-1 bg-green-500 rounded-full"></span>
                ✓ Ready
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-2 simple-slide-up transition-all duration-300 hover:bg-blue-100 dark:hover:bg-blue-900/50" style={{animationDelay: '0.9s'}}>
                <div className="text-sm text-blue-800 dark:text-blue-200">"Safety requirements?"</div>
              </div>
              
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-2 simple-slide-up transition-all duration-300 hover:bg-gray-200 dark:hover:bg-gray-600" style={{animationDelay: '1.1s'}}>
                <div className="flex items-start gap-2">
                  <div className="flex gap-1 mt-1">
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">Based on your manual...</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Animated Mobile Features Grid */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="text-center p-3 bg-white/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm enhanced-hover transition-all duration-300 simple-slide-up" style={{animationDelay: '1.3s'}}>
            <div className="w-10 h-10 mx-auto mb-2 bg-blue-500/20 rounded-lg flex items-center justify-center relative overflow-hidden breathe">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent translate-x-[-100%] animate-pulse"></div>
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-xs text-gray-800 dark:text-gray-100 mb-1">AI Analysis</h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs">Smart insights</p>
          </div>
          
          <div className="text-center p-3 bg-white/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm enhanced-hover transition-all duration-300 simple-slide-up" style={{animationDelay: '1.5s'}}>
            <div className="w-10 h-10 mx-auto mb-2 bg-green-500/20 rounded-lg flex items-center justify-center relative overflow-hidden breathe" style={{animationDelay: '1s'}}>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/30 to-transparent translate-x-[-100%] animate-pulse" style={{animationDelay: '1s'}}></div>
              <svg className="w-5 h-5 text-green-600 dark:text-green-400 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-xs text-gray-800 dark:text-gray-100 mb-1">Team Sync</h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs">Real-time</p>
          </div>
          
          <div className="text-center p-3 bg-white/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm enhanced-hover transition-all duration-300 simple-slide-up" style={{animationDelay: '1.7s'}}>
            <div className="w-10 h-10 mx-auto mb-2 bg-purple-500/20 rounded-lg flex items-center justify-center relative overflow-hidden breathe" style={{animationDelay: '2s'}}>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/30 to-transparent translate-x-[-100%] animate-pulse" style={{animationDelay: '2s'}}></div>
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-xs text-gray-800 dark:text-gray-100 mb-1">Secure</h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs">Enterprise</p>
          </div>
        </div>

        {/* Animated Mobile Stats */}
        <div className="grid grid-cols-3 gap-3 text-center mb-4">
          <div className="p-3 bg-white/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm simple-slide-up transition-all duration-300 hover:shadow-md hover:scale-105" style={{animationDelay: '1.9s'}}>
            <div className="text-lg font-bold text-blue-600 dark:text-blue-400 relative">
              50K+
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-xs">Systems</div>
          </div>
          <div className="p-3 bg-white/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm simple-slide-up transition-all duration-300 hover:shadow-md hover:scale-105" style={{animationDelay: '2.1s'}}>
            <div className="text-lg font-bold text-green-600 dark:text-green-400 relative">
              99.9%
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-xs">Uptime</div>
          </div>
          <div className="p-3 bg-white/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm simple-slide-up transition-all duration-300 hover:shadow-md hover:scale-105" style={{animationDelay: '2.3s'}}>
            <div className="text-lg font-bold text-purple-600 dark:text-purple-400 relative">
              24/7
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-xs">Support</div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <AnimatedModal isOpen={showForgotModal} onClose={() => { setShowForgotModal(false); setForgotMessage(""); setForgotError(""); setForgotEmail(""); }} className="w-full max-w-sm">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-0 overflow-hidden relative">
          {/* Blue/Indigo gradient header with icon */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-500 p-5 flex items-center gap-3">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 9.4V7A4.5 4.5 0 0 0 12 2.5 4.5 4.5 0 0 0 7.5 7v2.4M5 10.5v7A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-7a2.5 2.5 0 0 0-2.5-2.5h-9A2.5 2.5 0 0 0 5 10.5Z"/></svg>
            <h2 className="text-lg font-bold text-white">Reset Password</h2>
            <button
              onClick={() => setShowForgotModal(false)}
              className="ml-auto text-white/80 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center transition-colors duration-200"
              style={{marginTop: '-8px'}}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="p-7 pt-6">
            <p className="mb-4 text-gray-600 dark:text-gray-300 text-sm">Enter your email address and we'll send you a link to reset your password.</p>
            <input
              type="email"
              value={forgotEmail}
              onChange={e => { setForgotEmail(e.target.value); setForgotError(""); setForgotMessage(""); }}
              placeholder="Enter your email"
              className="w-full mb-3 p-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
            />
            {forgotError && <div className="mb-3 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{forgotError}</div>}
            {forgotMessage && <div className="mb-3 p-2 bg-green-100 border border-green-400 text-green-700 rounded text-sm">{forgotMessage}</div>}
            <button
              onClick={async () => {
                setForgotLoading(true);
                setForgotError("");
                setForgotMessage("");
                try {
                  await authAPI.forgotPassword(forgotEmail);
                  setForgotMessage("If an account with that email exists, a reset link has been sent.");
                } catch (err) {
                  setForgotError(err.message || "Failed to send reset link. Try again.");
                } finally {
                  setForgotLoading(false);
                }
              }}
              disabled={forgotLoading || !forgotEmail}
              className={`w-full py-3 rounded-xl font-semibold mt-2 transition text-white ${forgotLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-700 hover:to-blue-600"}`}
            >
              {forgotLoading ? "Sending..." : "Send Reset Link"}
            </button>
          </div>
        </div>
      </AnimatedModal>

      {/* Terms Modal */}
      <AnimatedModal isOpen={showTerms} onClose={() => setShowTerms(false)} className="w-full max-w-3xl">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-0 overflow-hidden relative">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-500 p-5 flex items-center gap-3">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M5 11h14M5 19h14M7 7h.01M17 7h.01M7 19h.01M17 19h.01"/></svg>
            <h2 className="text-lg font-bold text-white">Terms and Conditions</h2>
            <button
              onClick={() => setShowTerms(false)}
              className="ml-auto text-white/90 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="p-7 pt-6">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">Last Updated: {new Date().toLocaleDateString()}</div>
            <div className="max-h-[60vh] overflow-y-auto pr-1 hide-scrollbar">
              <div className="prose dark:prose-invert max-w-none text-sm">
                <p>Welcome to RAG AI (“we,” “our,” or “us”). These Terms and Conditions (“Terms”) govern your access to and use of our AI-powered Retrieval-Augmented Generation (RAG) application, website, APIs, and related services (collectively, the “Services”).</p>
                <p>By using the Services, you agree to be bound by these Terms. If you do not agree, you must stop using the Services immediately.</p>
                <h4>1. Definitions</h4>
                <p>“Services” – The RAG-based AI application, website, APIs, and related tools provided by RAG AI.</p>
                <p>“User” / “You” – Any individual or entity accessing or using the Services.</p>
                <p>“Content” – Any text, data, images, or other materials processed, generated, or uploaded to the Services.</p>
                <p>“Third-Party Data” – Information retrieved from external sources as part of the RAG process.</p>
                <h4>2. Eligibility</h4>
                <p>You must be at least 18 years old or have the consent of a legal guardian to use the Services. By using RAG AI, you confirm that you meet these eligibility requirements.</p>
                <h4>3. Account Registration</h4>
                <p>Some features require you to create an account. You are responsible for maintaining the confidentiality of your login credentials. You must notify us immediately of any unauthorized access or use of your account.</p>
                <h4>4. Use of the Services</h4>
                <ul className="list-disc pl-5">
                  <li>Use the Services only for lawful purposes.</li>
                  <li>Do not disrupt, damage, or impair the Services.</li>
                  <li>Do not upload malicious code, spam, or harmful content.</li>
                  <li>Do not attempt to reverse-engineer or exploit the Services without authorization.</li>
                </ul>
                <h4>5. AI and RAG Content Accuracy</h4>
                <p>RAG AI uses advanced Retrieval-Augmented Generation techniques to provide responses. No Guarantee of Accuracy – Responses may contain errors, outdated details, or incomplete information. You must independently verify any output before relying on it for critical decisions.</p>
                <h4>6. Third-Party Data and Links</h4>
                <p>RAG AI may retrieve and display data from third-party sources. We are not responsible for the accuracy, legality, or reliability of such sources. Links or references to external websites do not imply endorsement.</p>
                <h4>7. Intellectual Property</h4>
                <p>All intellectual property rights in RAG AI and the Services belong to us or our licensors. You retain ownership of the content you submit but grant us a non-exclusive, royalty-free, worldwide license to process it for providing the Services.</p>
                <h4>8. Prohibited Conduct</h4>
                <ul className="list-disc pl-5">
                  <li>Do not use the Services for illegal activities.</li>
                  <li>Do not infringe upon intellectual property rights.</li>
                  <li>Do not bypass security or access restrictions.</li>
                  <li>Do not automate usage in ways that overload or harm the system.</li>
                </ul>
                <h4>9. Privacy and Data Protection</h4>
                <p>Your use of RAG AI is also subject to our Privacy Policy, which explains how we collect, use, and protect your data.</p>
                <h4>10. Limitation of Liability</h4>
                <p>To the fullest extent permitted by law: We are not liable for indirect, incidental, or consequential damages. Our maximum liability shall not exceed the total amount you paid for the Services in the last [X] months.</p>
                <h4>11. Indemnification</h4>
                <p>You agree to indemnify and hold harmless RAG AI, its officers, employees, and partners from any claims, damages, or liabilities resulting from: Your use of the Services; Your violation of these Terms; Your infringement of third-party rights.</p>
                <h4>12. Termination</h4>
                <p>We may suspend or terminate your account at any time, without notice, if you breach these Terms.</p>
                <h4>13. Changes to the Terms</h4>
                <p>We may revise these Terms from time to time. The updated Terms will be posted on our website with a new “Last Updated” date. Continued use of the Services after changes means you accept the revised Terms.</p>
                <h4>14. Governing Law</h4>
                <p>These Terms are governed by the laws of your applicable jurisdiction, without regard to its conflict of laws principles.</p>
                <h4>15. Contact Information</h4>
                <p>RAG AI Support Team — Email: support@ragai.com</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setShowTerms(false)} className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700">Close</button>
              <button onClick={() => { setAgreedToTerms(true); setShowTerms(false); }} className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow">I Agree</button>
            </div>
          </div>
        </div>
      </AnimatedModal>
    </div>
  );
}
